import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Balance } from '../../../account/account.model';
import { AccountSettingsService } from '../../account-settings.service';
import { NotificationService } from '../../../shared/notification.service';
import { PackagePlan } from '../../../shared/models/plans';
import { Ng1StripeElementsService } from '../../../shared/ng1/ng1-stripe-elements.service';
import { AccountService } from '../../../shared/services/account.service';
import { PaymentService } from '../../../shared/services/payment.service';
import { InfoDialogComponent } from '../../../shared/dialog/info-dialog/info-dialog.component';
import { AmplitudeService } from 'app/shared/services/amplitude.service';
import { emailPattern } from '../../../shared/utils/email';
import { markFormAsTouched } from '../../../lead-generation/shared/reactive-forms/forEachElementInForm';

@Component({
  selector: 'cc-balance',
  templateUrl: './balance.component.html',
  styleUrls: ['./balance.component.scss']
})
export class BalanceComponent implements OnInit, OnDestroy {
  @Input() card;
  @Input() cardMetadata;
  public balance$: Observable<{
    balance: Balance;
  }> = new BehaviorSubject({ balance: { monetary: 0, voice: 0 } });
  public autoRechargeForm: FormGroup;
  public notificationsForm: any;
  public immediateChargeForm: any;
  public packagePlans: PackagePlan[];

  constructor(
    private formBuilder: FormBuilder,
    private accountSettingsService: AccountSettingsService,
    private notificationService: NotificationService,
    private ng1StripeElementsService: Ng1StripeElementsService,
    private accountService: AccountService,
    private paymentService: PaymentService,
    private dialog: MatDialog,
    private amplitudeService: AmplitudeService
  ) {}

  ngOnInit() {
    this.balance$ = this.accountSettingsService.getBalance();

    this.autoRechargeForm = this.formBuilder.group({
      enabled: false,
      amount: [1, Validators.min(1)]
    });
    this.notificationsForm = this.formBuilder.group({
      lowBalanceEnabled: false,
      lowBalanceThreshold: 10,
      email: ['', Validators.email]
    });
    this.immediateChargeForm = this.formBuilder.group({
      amount: 100
    });

    this.configureValidation();

    this.accountSettingsService.getNotificationSettings().subscribe(settings => {
      this.notificationsForm.patchValue(settings);
    });
    this.accountSettingsService.getAutoRechargeSettings().subscribe(settings => {
      console.log(settings);
      this.autoRechargeForm.patchValue(settings);
      console.log(this.autoRechargeForm.value);
    });
    this.accountSettingsService.getPackagePlans().subscribe(packagePlans => {
      this.packagePlans = packagePlans.sort((planA, planB) => planA.priceCents - planB.priceCents);
    });
  }

  submitNotifications() {
    if (this.notificationsForm.invalid) {
      markFormAsTouched(this.notificationsForm);
      return;
    }

    const notifications = this.notificationsForm.value;
    this.amplitudeService.logEvent('Panel:Account:Balance: Submit notifications', { notifications });
    this.accountSettingsService.saveNotifications(notifications).subscribe(() => {
      this.notificationService.open('SUCCESS_MSG');
    });
  }
  switchAutoRecharge(enabled: boolean): void {
    if (!this.cardMetadata.attachable || (this.card && this.card.brand)) {
      this.autoRechargeForm.controls.enabled.setValue(enabled);
      this.submitAutoRecharge();
    } else {
      this.amplitudeService.logEvent('Panel:Account:Balance: Cannot charge - no card');
      this.autoRechargeForm.controls.enabled.setValue(!enabled);
      this.dialog.open(InfoDialogComponent, {
        data: {
          title: 'PLANS.NO_CARD',
          body: 'PLANS.NO_CARD_INFO_BALANCE'
        }
      })
    }
  }

  submitAutoRecharge(): void {
    let amount = this.autoRechargeForm.value;
    if (this.autoRechargeForm.invalid) {
      this.amplitudeService.logEvent('Panel:Account:Balance: Invalid auto recharge', { amount });
      markFormAsTouched(this.autoRechargeForm);
      return;
    }
    this.amplitudeService.logEvent('Panel:Account:Balance: Submit auto recharge', { amount });
    this.accountSettingsService.saveAutoRecharge(amount).subscribe(() => {
      this.notificationService.open('SUCCESS_MSG');
    });
  }

  submitImmediateCharge() {
    const amount = this.immediateChargeForm.value.amount;
    this.amplitudeService.logEvent('Panel:Account:Balance: Charge', { amount });
    if (!this.cardMetadata.attachable || (this.card && this.card.brand)) {
      this.accountSettingsService.chargeImmediately({ amount }).subscribe((response: any) => {
        if (response.confirmationRedirectUrl) {
          window.location.href = response.confirmationRedirectUrl;
        } else if (response.piid) {
          this.paymentService.checkPaymentStatus(
            () => this.accountService.getPaymentStatus(response.piid),
            '/my-account'
          );
        } else {
          this.paymentService.redirect('/my-account');
        }
      });
    } else {
      this.amplitudeService.logEvent('Panel:Account:Balance: Cannot charge - no card');
      this.dialog.open(InfoDialogComponent, {
        data: {
          title: 'PLANS.NO_CARD',
          body: 'PLANS.NO_CARD_INFO_BALANCE'
        }
      });
    }
  }

  private configureValidation() {
    this.notificationsForm.get('lowBalanceEnabled').valueChanges.subscribe(lowBalanceEnabled => {
      const email = this.notificationsForm.get('email');
      email.setValidators(
        lowBalanceEnabled ? [Validators.required, Validators.pattern(emailPattern)] : Validators.pattern(emailPattern)
      );
      email.updateValueAndValidity();
    });
  }

  ngOnDestroy() {
    this.notificationService.dismiss();
  }
}
